import { Component } from '@theme/component';

if (!customElements.get('cm-quiz')) {
  class CMQuiz extends Component {
    static rank(products, answers) {
      const unique = [...new Map(products.map((product, index) => [String(product.id), { ...product, order: index }])).values()];
      const exact = unique.find(product => String(product.id) === answers.specific);
      const partners = exact?.partners?.map(String) || [];
      const matches = (value, answer) => answer && answer !== 'any' && String(value || '').split(',').map(item => item.trim()).includes(answer);
      return unique.map(product => ({
        ...product,
        exact: String(product.id) === answers.specific,
        partner: partners.indexOf(String(product.id)),
        score: (matches(product.universe, answers.universe) ? 6 : 0) + (matches(product.colors, answers.color) ? 3 : 0) + (matches(product.motifs, answers.motif) ? 2 : 0)
      })).sort((a, b) => Number(b.exact) - Number(a.exact) || Number(b.available) - Number(a.available) || Number(b.partner >= 0) - Number(a.partner >= 0) || (a.partner >= 0 && b.partner >= 0 ? a.partner - b.partner : 0) || b.score - a.score || a.order - b.order).slice(0, 3);
    }
    connectedCallback() {
      super.connectedCallback();
      this.initialize();
    }
    updatedCallback() {
      super.updatedCallback();
      this.controller?.abort();
      this.ready = false;
      this.initialize();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.ready = false;
      super.disconnectedCallback();
    }
    listen(element, event, handler) {
      element.addEventListener(event, handler, { signal: this.controller.signal });
    }
    initialize() {
      if (this.ready) return;
      this.controller?.abort();
      try {
        const data = JSON.parse(this.querySelector('[data-quiz-data]')?.textContent || '');
        const requiredStrings = ['quiz_step', 'quiz_next', 'quiz_finish', 'quiz_no_specific', 'quiz_explain', 'quiz_gift_explain', 'quiz_both_explain', 'quiz_match_exact', 'quiz_match_partner', 'quiz_match_preferences', 'quiz_result_count', 'sold_out'];
        if (!Array.isArray(data.products) || !data.strings || requiredStrings.some(key => typeof data.strings[key] !== 'string')) throw new Error('Invalid quiz configuration');
        const validProducts = new Map();
        for (const entry of data.products) {
          const product = entry?.product;
          if (!product || !Number.isSafeInteger(Number(product.id)) || !(Number(product.id) > 0)
            || typeof product.title !== 'string' || typeof product.handle !== 'string' || !Array.isArray(product.variants)
            || typeof entry.url !== 'string' || !entry.url) continue;
          const variants = product.variants.filter(variant => variant && Number.isSafeInteger(Number(variant.id)) && Number(variant.id) > 0
            && typeof variant.title === 'string' && typeof variant.available === 'boolean' && Number.isFinite(variant.price) && variant.price >= 0)
            .map(variant => ({ ...variant, id: Number(variant.id) }));
          if (!variants.length) continue;
          const { product: _, ...mapping } = entry;
          const id = Number(product.id);
          if (!validProducts.has(id)) validProducts.set(id, {
            ...product, ...mapping, id, variants,
            partners: Array.isArray(mapping.partners) ? mapping.partners.filter(partner => Number.isSafeInteger(Number(partner)) && Number(partner) > 0).map(Number) : [],
            available: variants.some(variant => variant.available && variant.price > 0),
          });
        }
        if (data.products.length && !validProducts.size) throw new Error('No valid quiz product data');
        const steps = [...this.querySelectorAll('[data-quiz-step]')];
        const requiredNodes = ['form', 'next', 'back', 'flow', 'results', 'empty', 'intro', 'edit', 'restart', 'counter', 'specific', 'specific-wrap', 'explanation', 'result-count', 'chips', 'grid', 'duo', 'gift'];
        if (steps.length !== 4 || requiredNodes.some(name => !this.querySelector(`[data-quiz-${name}]`))
          || steps.some(step => !step.querySelector('legend') || !step.querySelector('input[type="radio"]'))
          || !this.querySelector('[data-quiz-results]').querySelector('h1, h2')
          || typeof this.querySelector('[data-quiz-card]')?.content?.cloneNode !== 'function') throw new Error('Incomplete quiz markup');
        const format = new Intl.NumberFormat(this.dataset.locale || 'fr', { style: 'currency', currency: this.dataset.currency || 'EUR' });
        this.text = data.strings;
        this.products = [...validProducts.values()].filter(product => product.variants.some(variant => variant.price > 0));
        this.steps = steps;
        this.format = format;
        this.enhance();
        this.ready = true;
        const error = this.querySelector('[data-quiz-error]');
        if (error) error.hidden = true;
      } catch (error) {
        this.controller?.abort();
        this.ready = false;
        for (const name of ['flow', 'results', 'empty']) {
          const node = this.querySelector(`[data-quiz-${name}]`);
          if (node) node.hidden = true;
        }
        const intro = this.querySelector('[data-quiz-intro]');
        if (intro) intro.hidden = false;
        const message = this.querySelector('[data-quiz-error]');
        if (message) message.hidden = false;
        console.warn('[cm-quiz] Unable to initialize:', error);
      } finally {
        const loading = this.querySelector('[data-quiz-loading]');
        if (loading) loading.hidden = true;
      }
    }
    enhance() {
      this.controller = new AbortController();
      this.answers = {};
      this.step = 0;
      this.form = this.querySelector('[data-quiz-form]');
      this.next = this.querySelector('[data-quiz-next]');
      this.back = this.querySelector('[data-quiz-back]');
      this.flow = this.querySelector('[data-quiz-flow]');
      this.results = this.querySelector('[data-quiz-results]');
      this.querySelector('[data-quiz-empty]').hidden = this.products.length > 0;
      this.querySelector('[data-quiz-intro]').hidden = false;
      this.results.hidden = true;
      this.flow.hidden = this.products.length === 0;
      this.form.reset();
      this.started = false;
      if (!this.products.length) return;
      this.listen(this.form, 'change', event => {
        if (!(event.target instanceof HTMLElement)) return;
        const field = event.target.dataset.answer;
        if (field) {
          this.answers[field] = event.target.value;
          if (field === 'universe') { this.answers.specific = ''; this.renderSpecific(); }
          if (!this.started) { this.started = true; this.measure('start'); }
        }
        if (event.target.matches('[data-quiz-specific]')) this.answers.specific = event.target.value;
        this.next.disabled = !this.steps[this.step].querySelector('input:checked');
      });
      this.listen(this.form, 'submit', event => {
        event.preventDefault();
        if (this.next.disabled) return;
        if (this.step < this.steps.length - 1) { this.step += 1; this.renderStep(true); } else { this.renderResults(); }
      });
      this.listen(this.back, 'click', () => { this.step = Math.max(0, this.step - 1); this.renderStep(true); });
      this.listen(this.querySelector('[data-quiz-edit]'), 'click', () => {
        this.results.hidden = true;
        this.querySelector('[data-quiz-intro]').hidden = false;
        this.flow.hidden = false;
        this.step = 0;
        this.renderStep(true);
      });
      this.listen(this.querySelector('[data-quiz-restart]'), 'click', () => {
        this.answers = {};
        this.started = false;
        this.form.reset();
        this.results.hidden = true;
        this.querySelector('[data-quiz-intro]').hidden = false;
        this.flow.hidden = false;
        this.step = 0;
        this.renderSpecific();
        this.renderStep(true);
      });
      this.renderStep(false);
    }
    renderStep(focus) {
      this.steps.forEach((step, index) => { step.hidden = index !== this.step; });
      this.querySelectorAll('.cm-quiz__progress li').forEach((item, index) => {
        item.classList.toggle('is-complete', index < this.step);
        if (index === this.step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
      });
      this.querySelector('[data-quiz-counter]').textContent = this.text.quiz_step.replace('[step]', String(this.step + 1));
      this.back.hidden = this.step === 0;
      this.next.textContent = this.step === this.steps.length - 1 ? this.text.quiz_finish : this.text.quiz_next;
      this.next.disabled = !this.steps[this.step].querySelector('input:checked');
      if (focus) this.steps[this.step].querySelector('legend').focus();
    }
    renderSpecific() {
      const specific = this.querySelector('[data-quiz-specific]');
      specific.replaceChildren(new Option(this.text.quiz_no_specific, ''));
      const choices = this.products.filter(product => product.universe === this.answers.universe);
      const show = ['cryptos', 'dex'].includes(this.answers.universe) && choices.length > 0;
      if (show) choices.forEach(product => specific.add(new Option(product.title, String(product.id))));
      this.querySelector('[data-quiz-specific-wrap]').hidden = !show;
      specific.value = this.answers.specific || '';
    }
    renderResults() {
      const models = CMQuiz.rank(this.products, this.answers);
      this.querySelector('[data-quiz-intro]').hidden = true;
      this.flow.hidden = true;
      this.results.hidden = false;
      const explanation = this.answers.recipient === 'gift' ? 'quiz_gift_explain' : this.answers.recipient === 'both' ? 'quiz_both_explain' : 'quiz_explain';
      this.querySelector('[data-quiz-explanation]').textContent = this.text[explanation];
      this.querySelector('[data-quiz-result-count]').textContent = this.text.quiz_result_count.replace('[count]', String(models.length));
      const chips = this.querySelector('[data-quiz-chips]');
      chips.replaceChildren();
      this.form.querySelectorAll('input:checked').forEach(input => {
        const chip = document.createElement('span');
        chip.textContent = input.parentElement.querySelector('span:nth-of-type(2)').textContent;
        chips.append(chip);
      });
      const grid = this.querySelector('[data-quiz-grid]');
      grid.replaceChildren();
      models.forEach(product => {
        const card = this.querySelector('[data-quiz-card]').content.cloneNode(true);
        card.querySelector('[data-result-title]').textContent = product.title;
        card.querySelector('[data-result-reason]').textContent = this.text[product.exact ? 'quiz_match_exact' : product.partner >= 0 ? 'quiz_match_partner' : 'quiz_match_preferences'];
        const variant = product.variants.find(item => item.available && item.price > 0) || product.variants.find(item => item.price > 0);
        card.querySelector('[data-result-price]').textContent = this.format.format(variant.price / 100);
        const stock = card.querySelector('[data-result-stock]');
        stock.textContent = product.available ? '' : this.text.sold_out;
        stock.hidden = product.available;
        const image = card.querySelector('[data-result-image]');
        if (product.image) { image.src = product.image; image.alt = product.title; } else { image.parentElement.hidden = true; }
        card.querySelectorAll('[data-result-link]').forEach(link => {
          link.href = product.url;
          this.listen(link, 'click', () => this.measure('product', [product.id]));
        });
        grid.append(card);
      });
      this.querySelector('[data-quiz-duo]').hidden = this.answers.recipient !== 'both';
      this.querySelector('[data-quiz-gift]').hidden = this.answers.recipient !== 'gift';
      this.results.querySelector('h1, h2').focus();
      this.measure('results', models.map(product => product.id));
    }
    measure(action, products = []) {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
      this.dispatchEvent(new CustomEvent(`cm:quiz-${action}`, { bubbles: true, detail: { products } }));
    }
  }
  customElements.define('cm-quiz', CMQuiz);
}
